// Per-device progress, kept only in localStorage.

import type { Tile } from './scoring'

export interface Attempt {
  words: string[]
  score: number
  /** Where the play went, so it can be shown on the board afterwards (saved since Oct 2026). */
  tiles?: Tile[]
}

/** The highest-scoring valid placement tried so far, played or not. */
export interface BestFound extends Attempt {
  tiles: Tile[]
}

export interface DayRecord {
  date: string
  attempts: Attempt[]
  finished: boolean
  /** Whether the final score has been sent to the anonymous stats API. */
  submitted: boolean
  /** Whether the anonymous "started" event has been sent. */
  startedSent?: boolean
  /** Played from the archive after its day; excluded from streaks and stats. */
  late?: boolean
  bestFound?: BestFound
}

const KEY = 'wordevery.history.v1'

export function readAll(): Record<string, DayRecord> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, DayRecord>
  } catch {
    return {}
  }
}

export function loadDay(date: string, today: string = todayUTC()): DayRecord {
  return readAll()[date] ?? { date, attempts: [], finished: false, submitted: false, late: date < today }
}

export function saveDay(record: DayRecord): void {
  try {
    const all = readAll()
    all[record.date] = record
    localStorage.setItem(KEY, JSON.stringify(all))
  } catch {
    // Storage full or disabled: progress just won't persist.
  }
}

export function bestScore(record: DayRecord): number {
  return record.attempts.reduce((max, a) => Math.max(max, a.score), 0)
}

/** Whether this device finished any puzzle before the given day. */
export function hasFinishedBefore(date: string, all: Record<string, DayRecord> = readAll()): boolean {
  return Object.values(all).some((d) => d.finished && d.date < date)
}

export interface Summary {
  played: number
  currentStreak: number
  maxStreak: number
}

export function summarize(today: string, all: Record<string, DayRecord> = readAll()): Summary {
  const days = Object.values(all)
    .filter((d) => d.finished && !d.late)
    .map((d) => d.date)
    .sort()
  let maxStreak = 0
  let run = 0
  let prev: string | null = null
  for (const d of days) {
    run = prev && addDays(prev, 1) === d ? run + 1 : 1
    maxStreak = Math.max(maxStreak, run)
    prev = d
  }
  const last = days[days.length - 1]
  const current = last === today || last === addDays(today, -1) ? run : 0
  return { played: days.length, currentStreak: current, maxStreak }
}

export function addDays(date: string, n: number): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

export function todayUTC(): string {
  return new Date().toISOString().slice(0, 10)
}
